import { useState, useEffect } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { Container } from 'react-bootstrap'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { apiClient } from "./services/api/client"
import AppRoutes from './AppRoutes'
import { clearAuthState, startAuthSessionMonitor, stopAuthSessionMonitor } from './services/auth/session'

const App = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const hasAuthenticatedUser = Boolean(user)

  useEffect(() => {
    const verifyUser = async () => {
      try {
        const data = await apiClient.post("/login/verify", {})
        if (!data?.user) {
          handleLogout()
          return
        }
        setUser(data.user)
        sessionStorage.setItem("user", JSON.stringify(data.user))
      } catch (error) {
        if (error?.status !== 401) {
          console.error("Ověření přihlášení selhalo:", error)
        }
        handleLogout()
      } finally {
        setLoading(false)
      }
    }

    const storedUser = sessionStorage.getItem("user")
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        // ignore parse errors
      }
    }

    verifyUser()
  }, [])

  useEffect(() => {
    if (!user) {
      stopAuthSessionMonitor()
      return
    }

    const stopMonitor = startAuthSessionMonitor()
    return () => stopMonitor()
  }, [user])

  useEffect(() => {
    const manifestId = 'internal-app-manifest'
    const existingManifest = document.getElementById(manifestId)

    if (!hasAuthenticatedUser) {
      existingManifest?.remove()
      return
    }

    if (existingManifest) return

    const manifest = document.createElement('link')
    manifest.id = manifestId
    manifest.rel = 'manifest'
    manifest.href = `${process.env.PUBLIC_URL || ''}/site.webmanifest`
    document.head.appendChild(manifest)

    return () => manifest.remove()
  }, [hasAuthenticatedUser])

  useEffect(() => {
    if (!user) return

    const intervalId = setInterval(async () => {
      try {
        const data = await apiClient.post("/login/verify", {})
        if (!data?.user) {
          handleLogout()
          return
        }
        setUser(data.user)
        sessionStorage.setItem("user", JSON.stringify(data.user))
      } catch (error) {
        if (error?.status !== 401) {
          console.error("Průběžné ověření přihlášení selhalo:", error)
        }
        handleLogout()
      }
    }, 300000)

    return () => clearInterval(intervalId)
  }, [user])

  const handleLogout = () => {
    stopAuthSessionMonitor()
    clearAuthState()
    setUser(null)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className='loader-container'>
        <div className="loader"></div>
      </div>
    )
  }

  return (
    <Router basename={process.env.PUBLIC_URL || "/"} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Container fluid className='p-0 m-0'>
        <DndProvider backend={HTML5Backend}>
          <AppRoutes user={user} setUser={setUser} />
        </DndProvider>
      </Container>
    </Router>
  )
}

export default App
